import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import Avatar from '@components/avatar';
import parse from 'html-react-parser';
import { BASE_IMG_URL } from '@images/imageUrls';
import classNames from 'classnames';
import EmojiPicker from '@components/EmojiPicker/h5';
import { Image, Spin } from 'antd';
import { getEmojiDisplayValue, isEmojiImageUrl } from '@utils/emoji';
interface RouteParams {
    code?: string;
}
interface PrivateMessageH5PageProps extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
    userInfo?: any
    unreadNotificationsData?: any
    handleLoadMore: any
    userChatList?: any[]
    privateUserInfo?: any   // URL usercode 对应的用户信息
    chatScrollTrigger?: number  // 递增时触发聊天区滚动到底部
    submitNotificationsReadData?: () => Promise<void>
    // 聊天输入相关 props（由父组件管理）
    chatInputContent?: string
    chatEmojiPickerVisible?: boolean
    chatUploading?: boolean
    chatSendLoading?: boolean
    onChatInputChange?: (content: string) => void
    onChatEmojiToggle?: () => void
    onChatEmojiClose?: () => void
    onChatFileUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void
    onChatSend?: () => void
}
interface PrivateMessageH5State {
    loading: boolean;
    error: any
    pageLoaded: any
    photoMenuVisible: boolean
    // navList: any
}
@inject('app', 'site', 'user', 'thread')
@observer
class PrivateMessageH5Page extends BasePage<PrivateMessageH5PageProps, PrivateMessageH5State> {
    private emojiMapCache: Map<string, { value: string; isImage: boolean; category?: string }> | null = null;
    private emojiListStamp: any = null;
    private chatBoxRef = React.createRef<HTMLDivElement>();
    private fileInputRef = React.createRef<HTMLInputElement>();
    private cameraInputRef = React.createRef<HTMLInputElement>();
    private textareaRef = React.createRef<HTMLInputElement>();
    private prevChatScrollTrigger: number = 0;
    private prevScrollHeight: number = 0;       // 加载更多前记录 scrollHeight，用于恢复位置
    private isLoadingMore: boolean = false;      // 防止重复触发加载更多
    private prevUserChatLength: number = 0;      // 记录上一次 userChatList 长度
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        photoMenuVisible: false,

    };
    componentDidMount() {
        const { app, user, thread } = this.props;

        super.componentDidMount();
    }

    componentDidUpdate() {
        const { chatScrollTrigger, userChatList } = this.props;
        const box = this.chatBoxRef.current;
        if (!box) return;

        const currentLength = userChatList?.length || 0;

        if (chatScrollTrigger !== this.prevChatScrollTrigger) {
            // 首次加载 / 切换房间：滚动到底部
            box.scrollTop = box.scrollHeight;
            this.prevChatScrollTrigger = chatScrollTrigger ?? 0;
        } else if (currentLength > this.prevUserChatLength && this.prevScrollHeight > 0) {
            // 加载更多历史数据（数据 prepend 到顶部）：恢复滚动位置
            box.scrollTop = box.scrollHeight - this.prevScrollHeight;
            this.prevScrollHeight = 0;
            this.isLoadingMore = false;
        }

        this.prevUserChatLength = currentLength;
    }

    // 聊天区滚动事件：触顶时加载更多历史消息
    handleChatScroll = () => {
        const box = this.chatBoxRef.current;
        if (!box || this.isLoadingMore) return;

        if (box.scrollTop <= 0) {
            this.isLoadingMore = true;
            this.prevScrollHeight = box.scrollHeight;
            this.props.handleLoadMore?.();
        }
    }

    // 表情选择：读取 textarea 光标位置，计算新内容后通知父组件
    handleEmojiSelect = (emojiValue: string, emojiCode?: string) => {
        // 图片表情发送 code，原生 Unicode emoji 直接发送字符，避免把字符当图片地址。
        const value = isEmojiImageUrl(emojiValue) ? (emojiCode || emojiValue) : (emojiValue || emojiCode || '');
        const textarea = this.textareaRef.current;
        const cursorPos = textarea?.selectionStart ?? 0;
        const { chatInputContent = '' } = this.props;
        const newContent = chatInputContent.slice(0, cursorPos) + value + chatInputContent.slice(cursorPos);

        this.props.onChatInputChange?.(newContent);
        this.props.onChatEmojiClose?.();

        // 恢复光标位置
        setTimeout(() => {
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(cursorPos + value.length, cursorPos + value.length);
            }
        }, 0);
    }

    // 处理文件选择，转发给父组件（上传后直接发送图片消息）
    handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ photoMenuVisible: false });
        this.props.onChatFileUpload?.(e);
        // 清除 input 值，确保再次选择同一文件时也能触发 change 事件
        e.target.value = '';
    }

    // 切换图片/拍照菜单
    handlePhotoMenuToggle = () => {
        this.setState(prev => ({ photoMenuVisible: !prev.photoMenuVisible }));
    }

    // 关闭图片/拍照菜单
    handlePhotoMenuClose = () => {
        this.setState({ photoMenuVisible: false });
    }

    // 选择相册图片
    handleSelectAlbum = () => {
        this.fileInputRef.current?.click();
    }

    // 选择拍照
    handleSelectCamera = () => {
        this.cameraInputRef.current?.click();
    }

    // 处理文本输入变化
    handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.onChatInputChange?.(e.target.value);
    }

    private escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    private getEmojiMap = () => {
        const emojiList = this.props.site?.emojiList || [];
        if (this.emojiListStamp === emojiList && this.emojiMapCache) return this.emojiMapCache;

        const map = new Map<string, { value: string; isImage: boolean; category?: string }>();
        emojiList.forEach((cat: any) => {
            (cat?.emoji || []).forEach((emoji: any) => {
                const value = getEmojiDisplayValue(emoji);
                if (emoji?.code && value) {
                    map.set(emoji.code, {
                        value,
                        isImage: isEmojiImageUrl(value),
                        category: cat?.category,
                    });
                }
            });
        });
        this.emojiMapCache = map;
        this.emojiListStamp = emojiList;
        return map;
    };

    private replaceEmojiInContent = (content?: string, useSmall: boolean = false) => {
        if (!content) return content || '';
        const map = this.getEmojiMap();
        if (!map || map.size === 0) return content;
        let result = content;
        map.forEach((info, code) => {
            const reg = new RegExp(this.escapeRegex(code), 'g');
            if (info.isImage) {
                // useSmall=true 时用小表情（评论区），false时根据category判断（正文）
                const cls = useSmall ? styles.emojiInlineSmall : (info.category === '默认' ? styles.emojiInlineSmall : styles.emojiInlineLarge);
                const imgTag = `<img src="${info.value}" alt="${code}" class="${cls}" />`;
                result = result.replace(reg, () => imgTag);
            } else {
                result = result.replace(reg, () => info.value);
            }
        });
        return result;
    };

    // 私信聊天记录
    renderUserChatDetail = () => {
        const { user, userChatList } = this.props;
        if (!user || !userChatList) {
            return null;
        }
        let options = {};
        const parserOptions = {
            replace: (domNode: any) => {
                // 可以在这里自定义节点替换逻辑
            },
            ...options
        };
        return (
            <div className={styles.chatDetailOuterBox} ref={this.chatBoxRef} onScroll={this.handleChatScroll}>
                <div className={styles.chatDetailBox}>
                    {userChatList.map((item, index) => {
                        const isMyMessage = item.sender?.code === user.userInfo?.code;
                        const isImageMessage = item.imgUrl && item.type === 2;
                        // 渲染消息内容
                        const renderMessageContent = () => {
                            if (isImageMessage) {
                                return (
                                    <Image
                                        className={styles.messageImg}
                                        src={item.imgUrl}
                                        alt="message"
                                        preview={{ mask: '预览' }}
                                    />
                                );
                            }

                            return (
                                <div className={styles.messageContentBox}>
                                    <div className={styles.content}>{parse(this.replaceEmojiInContent(item.content, true), parserOptions)}</div>
                                </div>
                            );
                        };

                        if (isMyMessage) {
                            return (
                                <div key={index}>
                                    {item.timestamp && <div className={styles.timestamp}>{item.timestamp}</div>}
                                    <div className={styles.privateRightBox}>
                                        <div className={styles.privateContent}>{renderMessageContent()}</div>
                                        <Avatar
                                            avatarUrl={item.sender?.avatar}
                                            userCode={item.sender?.code}
                                            size={60}
                                            showOfficialTag={false}
                                        ></Avatar>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={index}>
                                {item.timestamp && <div className={styles.timestamp}>{item.timestamp}</div>}
                                <div className={styles.privateLeftBox}>
                                    <Avatar
                                        avatarUrl={item.sender?.avatar}
                                        userCode={item.sender?.code}
                                        size={60}
                                        showOfficialTag={false}
                                    ></Avatar>
                                    <div className={styles.privateContent}>{renderMessageContent()}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    render() {
        const { photoMenuVisible } = this.state;
        return (
            <div className={styles.wrapper}>
                {this.renderUserChatDetail()}
                <div className={styles.privateSendOuterBox}>
                    <div className={styles.privateSendBox}>
                        <input
                            ref={this.textareaRef}
                            placeholder='请输入内容'
                            className={styles.privateInput}
                            value={this.props.chatInputContent || ''}
                            onChange={this.handleInputChange}
                            onFocus={this.handlePhotoMenuClose}
                        />
                        {this.props.chatEmojiPickerVisible ? (
                            <img
                                src={`${BASE_IMG_URL}/message/icon10.png`}
                                alt="键盘"
                                className={styles.operaIcon}
                                onClick={this.props.onChatEmojiToggle}
                            />
                        ) : (
                            <img
                                src={`${BASE_IMG_URL}/message/icon8.png`}
                                alt="添加表情"
                                className={styles.operaIcon}
                                onClick={this.props.onChatEmojiToggle}
                            />
                        )}
                        <img
                            src={`${BASE_IMG_URL}/message/icon9.png`}
                            alt="上传图片"
                            className={styles.operaIcon}
                            onClick={this.handlePhotoMenuToggle}
                        />
                        <div
                            className={classNames(styles.sendBtn, { [styles.sendBtnDisable]: !(this.props.chatInputContent || '').trim() })}
                            onClick={this.props.onChatSend}
                        >发送</div>
                    </div>
                    <input
                        ref={this.fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={this.handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <input
                        ref={this.cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={this.handleFileChange}
                        style={{ display: 'none' }}
                    />
                    {photoMenuVisible && (<div className={styles.photoMenuBox}>
                        <>
                            {/* <div className={styles.photoMenuMask} onClick={this.handlePhotoMenuClose} /> */}
                            <div className={styles.photoMenu}>
                                <div className={styles.photoMenuItem} onClick={this.handleSelectAlbum}>
                                    <div className={styles.menu}>
                                        <img
                                            src={`${BASE_IMG_URL}/message/icon12.png`}
                                            alt="添加表情"
                                            className={styles.icon}
                                        />
                                        <div className={styles.text}>图片</div>
                                    </div>
                                    
                                </div>
                                <div className={styles.photoMenuItem} onClick={this.handleSelectCamera}>
                                    <div className={styles.menu}>
                                        <img
                                            src={`${BASE_IMG_URL}/message/icon13.png`}
                                            alt="添加表情"
                                            className={styles.icon}
                                        />
                                        <div className={styles.text}>拍照</div>
                                    </div>
                                    
                                </div>
                                {/* <div className={styles.photoMenuCancel} onClick={this.handlePhotoMenuClose}>取消</div> */}
                            </div>
                        </>
                    </div>)}
                    {/* 表情选择器 */}
                    {this.props.chatEmojiPickerVisible && (
                        <EmojiPicker
                            visible={this.props.chatEmojiPickerVisible}
                            onClose={this.props.onChatEmojiClose || (() => {})}
                            onSelect={this.handleEmojiSelect}
                            inFlow={true}
                            showEmojiTab
                        />
                    )}
                </div>
            </div>
        );
    }
}

export default withRouter(PrivateMessageH5Page);
